import { Button } from "@mui/material";
import { useSetAtom } from "jotai";
import { type FC, useCallback, useContext } from "react";
import { type FieldErrors, useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { SidebarContext } from "../../../../utils/contexts/sidebar-context";
import { useAnalytics } from "../../../analytics/analytics";
import { useAccountLimitsContext } from "../../../context/account-limit-context";
import { SegmentStateService } from "../../../segment/server/segment-state-service";
import type { MomentFormMoment } from "../../types/moment-form-moment";
import { segmentStateAtom } from "../../types/store";
import type { SaveButtonProps } from "./types";

export const SaveDraftButton: FC<Omit<SaveButtonProps, "employeeCountState">> = ({
  formSaveState: {
    saving,
    setSaving,
    formDisabled: { "draft-button": disabled },
    setLastAction,
  },
  handleSave,
  saveDataReady,
}) => {
  const { getValues, handleSubmit } = useFormContext<MomentFormMoment>();
  const { setProgramsDirty } = useContext(SidebarContext);
  const { gaEvent } = useAnalytics();
  const { allow_all_and_upcoming_segment } = useAccountLimitsContext();

  const setSegmentStateAtom = useSetAtom(segmentStateAtom);

  const reloadSegments = useSupabaseCallback(
    async ({ supabase }, moment: MomentFormMoment) => {
      const segmentState = await new SegmentStateService(supabase).load(
        moment.id,
        moment.account_id,
        moment.segment?.id,
        allow_all_and_upcoming_segment ?? false,
      );

      setSegmentStateAtom(segmentState);
    },
    [allow_all_and_upcoming_segment, setSegmentStateAtom],
  );

  const saveDraft = useCallback(
    async (values: MomentFormMoment): Promise<void> => {
      gaEvent("save_draft", { event_category: "moment" });
      setSaving(true);

      const saved = await handleSave(values);
      if (saved?.status === "valid") {
        setProgramsDirty(true);
        toast.success("Communication Saved");

        // reload segments
        void reloadSegments(values);
      } else if (saved?.status === "error") {
        toast.error("Something went wrong");
      }
      setSaving(false);
      setLastAction("draft");
    },
    [gaEvent, setSaving, handleSave, setLastAction, setProgramsDirty, reloadSegments],
  );

  const saveDraftErrorHandler = useCallback(
    async (errs: FieldErrors<MomentFormMoment>): Promise<void> => {
      gaEvent("save_draft_error", { event_category: "moment" });
      setSaving(true);

      if (errs.title || errs.program_id) {
        setSaving(false);
        errs.title?.message && toast.error(errs.title.message);
        errs.program_id?.message && toast.error(errs.program_id.message);
        return;
      }

      if (errs.additional_recipients)
        errs.additional_recipients?.message && toast.error(errs.additional_recipients.message);

      const values = getValues();
      await saveDraft(values);
    },
    [getValues, saveDraft, setSaving, gaEvent],
  );

  return (
    <Button
      data-analytics-id="moment-form-save-draft"
      sx={{ ml: 2 }}
      loading={saving}
      variant="outlined"
      disabled={disabled || !saveDataReady}
      onClick={() => void handleSubmit(saveDraft, saveDraftErrorHandler)().catch(console.error)}
      id="saveDraftButton"
    >
      Save
    </Button>
  );
};
